/**
 * All of GLOPE's sounds, synthesised with Web Audio (no files to download).
 * Browsers only allow sound after the first tap, so everything here is
 * silently skipped until `unlock()` has run from a user gesture.
 */
export class Sound {
  private ctx: AudioContext | null = null;
  private out: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private mute = false;

  constructor(muted = false) {
    this.mute = muted;
    // Mix with the user's music and respect the iPhone silent switch (Safari 17+).
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session) session.type = 'ambient';
  }

  unlock(): void {
    try {
      if (!this.ctx) {
        this.ctx = new AudioContext();
        this.out = this.ctx.createGain();
        this.out.gain.value = this.mute ? 0 : 0.6;
        this.out.connect(this.ctx.destination);
        const n = this.ctx.sampleRate * 0.6;
        this.noise = this.ctx.createBuffer(1, n, this.ctx.sampleRate);
        const d = this.noise.getChannelData(0);
        for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
      }
      void this.ctx.resume();
    } catch {
      /* no audio */
    }
  }

  get muted(): boolean {
    return this.mute;
  }

  set muted(m: boolean) {
    this.mute = m;
    if (this.out && this.ctx) this.out.gain.setTargetAtTime(m ? 0 : 0.6, this.ctx.currentTime, 0.02);
  }

  private ready(): AudioContext | null {
    return !this.mute && this.ctx && this.ctx.state === 'running' ? this.ctx : null;
  }

  /** "It's your turn!" */
  attention(): void {
    const c = this.ready();
    if (!c) return;
    for (const [i, f] of [660, 880, 1320].entries()) this.tone('square', f, c.currentTime + i * 0.12, 0.11, 0.1);
  }

  /** Countdown beep: 3, 2, 1. */
  tick(): void {
    const c = this.ready();
    if (c) this.tone('square', 587, c.currentTime, 0.14, 0.12);
  }

  /** GO! An octave up, held longer, with a little upward kick. */
  go(): void {
    const c = this.ready();
    if (!c) return;
    const t = c.currentTime;
    this.tone('square', 1175, t, 0.4, 0.12, 1.06);
    this.tone('triangle', 587, t, 0.4, 0.12, 1.06);
  }

  /** Coin: the classic two-note bling. */
  coin(): void {
    const c = this.ready();
    if (!c) return;
    const t = c.currentTime;
    this.tone('square', 988, t, 0.06, 0.07);
    this.tone('square', 1319, t + 0.06, 0.22, 0.07);
  }

  /** Passed one of the last players: a quick rising fanfare over a crowd "ooh". */
  beat(volume = 1): void {
    const c = this.ready();
    if (!c || !this.out) return;
    const t = c.currentTime;
    const bus = c.createGain();
    bus.gain.value = volume;
    bus.connect(this.out);
    for (const [i, f] of [523, 659, 784].entries()) this.tone('square', f, t + i * 0.07, 0.09, 0.08, 1, bus);
    this.tone('square', 1047, t + 0.21, 0.45, 0.09, 1.02, bus);
    this.tone('triangle', 523, t + 0.21, 0.45, 0.12, 1, bus);
    this.noiseBurst(bus, t + 0.05, 0.8, 'bandpass', 600, 900, 1.5, 0.25);
  }

  /** The runner goes splat: thump, squelch, splash, then drops landing. */
  splat(volume = 1): void {
    const c = this.ready();
    if (!c || !this.out || !this.noise) return;
    const t = c.currentTime;
    const bus = c.createGain();
    bus.gain.value = volume;
    bus.connect(this.out);

    // Thump: a sine dropping fast.
    const o = c.createOscillator();
    const og = c.createGain();
    o.frequency.setValueAtTime(170, t);
    o.frequency.exponentialRampToValueAtTime(40, t + 0.25);
    og.gain.setValueAtTime(0.9, t);
    og.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    o.connect(og).connect(bus);
    o.start(t);
    o.stop(t + 0.32);

    // Squelch: noise through a narrow, falling band-pass.
    this.noiseBurst(bus, t, 0.22, 'bandpass', 1400, 220, 7, 0.9);
    // Splash: wide noise, darkening as it settles.
    this.noiseBurst(bus, t + 0.02, 0.5, 'lowpass', 4000, 300, 0.7, 0.45);

    // Drops landing all around.
    for (let i = 0; i < 5; i++) {
      const at = t + 0.18 + Math.random() * 0.5;
      const f = 700 + Math.random() * 900;
      const d = c.createOscillator();
      const g = c.createGain();
      d.type = 'sine';
      d.frequency.setValueAtTime(f, at);
      d.frequency.exponentialRampToValueAtTime(f * 0.45, at + 0.06);
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.18, at + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, at + 0.07);
      d.connect(g).connect(bus);
      d.start(at);
      d.stop(at + 0.08);
    }
  }

  private noiseBurst(dest: AudioNode, t: number, dur: number, type: BiquadFilterType, f0: number, f1: number, q: number, vol: number): void {
    const c = this.ctx!;
    const src = c.createBufferSource();
    src.buffer = this.noise;
    const f = c.createBiquadFilter();
    f.type = type;
    f.Q.value = q;
    f.frequency.setValueAtTime(f0, t);
    f.frequency.exponentialRampToValueAtTime(f1, t + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(f).connect(g).connect(dest);
    src.start(t);
    src.stop(t + dur);
  }

  private tone(type: OscillatorType, freq: number, t: number, dur: number, vol: number, bend = 1, dest: AudioNode = this.out!): void {
    const c = this.ctx!;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (bend !== 1) o.frequency.exponentialRampToValueAtTime(freq * bend, t + dur);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g).connect(dest);
    o.start(t);
    o.stop(t + dur + 0.02);
  }
}
